/**
 * Tap-the-gutter-to-close. ✕ v7.69 (per Javi: "remove the tap anywhere to go
 * back and instead put a close X at the top right corner... it's not user
 * friendly"): only a press that starts AND ends on the dark gutter around a
 * card closes it. A tap on the card - its text, its art, its whitespace, a
 * grayed-out control - never does; the ModalClose button at the card's
 * corner (and each card's own buttons) do. The v0.73 rule that any
 * non-interactive tap dismissed, and v0.76's "a disabled control closes",
 * are gone with it.
 *
 * Bind the handlers to the backdrop root WITHOUT `.self` (the composable
 * tells the gutter from the card itself):
 *
 *   <div @pointerdown="dismiss.down" @pointerup="dismiss.up">
 *
 * Scrolling never closes: a press that drifts past the tolerance is a
 * scroll, not a tap. `data-interactive` attributes on cards are harmless
 * leftovers from the tap-anywhere days and may stay.
 */

/** 🎓 v7.39 (per Javi, F08): a dialog opened under the finger never closes on
 * that finger's next tap. A double-tap on a mission marker opened the sheet on
 * tap 1 and dismissed it on tap 2 (4/4, 120-450 ms apart - so 500, not 400).
 * For this long after a dialog appears, a press on its backdrop cannot
 * dismiss it; its buttons work at once (they never come through here). */
export const OPEN_GRACE_MS = 500

/** The last pointerup anywhere on the page, with everything that was under
 * the finger then. For a dialog whose composable lives as long as its page
 * (index.vue, collection.vue, GameMenu), mount time says nothing - there, a
 * recent tap is the one that opened it only if the dialog was NOT under it
 * yet. One that was (the tap that closed a dialog stacked on top of it) opened
 * nothing: "back, back" still closes both. */
let lastUp: { at: number; target: EventTarget | null; under: Element[] } | null = null
let trackingUps = false
function trackPointerUps() {
  if (trackingUps || typeof document === 'undefined') return
  trackingUps = true
  document.addEventListener('pointerup', (event) => {
    lastUp = { at: performance.now(), target: event.target, under: document.elementsFromPoint(event.clientX, event.clientY) }
  }, { capture: true, passive: true })
}

/** 🎓 v7.39 (per Javi, D6a): when a card over the node screen last opened or
 * closed. The board ignores its ×3 toggle for a moment after (ForestBoard):
 * the second tap of the double-tap that closed a card is not a board tap. */
export function useOverlayStamp() {
  const at = useState('lastOverlayChange', () => 0)
  return {
    stamp: () => { if (import.meta.client) at.value = performance.now() },
    since: () => (import.meta.client ? performance.now() - at.value : Number.POSITIVE_INFINITY),
  }
}

/** A press that starts and ends on the gutter is an unambiguous "close" -
 * allow a fat-finger drift (v1.73, per Javi: taps to go back sometimes did
 * nothing on the phone). */
const GUTTER_TOLERANCE = 80

export function useBackdropDismiss(onDismiss: () => void) {
  let downAt: { x: number; y: number } | null = null
  // 🎓 v7.39: dialogs mounted with v-if open when their component mounts.
  let mountedAt = Number.NEGATIVE_INFINITY
  if (getCurrentInstance()) onMounted(() => { mountedAt = performance.now() })
  if (import.meta.client) trackPointerUps()

  /** Inside the open grace: just mounted, or opened by a tap it was not under. */
  function justOpened(event: PointerEvent): boolean {
    const now = performance.now()
    if (now - mountedAt < OPEN_GRACE_MS) return true
    const up = lastUp
    if (!up || now - up.at >= OPEN_GRACE_MS) return false
    const backdrop = event.currentTarget
    if (!(backdrop instanceof Element)) return false
    if (up.under.includes(backdrop)) return false
    return !(up.target instanceof Node && backdrop.contains(up.target))
  }

  function down(event: PointerEvent) {
    downAt = event.target !== event.currentTarget || justOpened(event)
      ? null
      : { x: event.clientX, y: event.clientY }
  }

  /** Mobile browsers cancel a pointer they claim (scroll, gesture); a
   * cancelled press must never pair with a later up (v1.73). */
  function cancel() {
    downAt = null
  }

  /**
   * Ghost-click guard (v0.96, from Javi's report): the dismiss fires on
   * pointerup and unmounts the backdrop — but the browser then fires the
   * synthesized `click` at the SAME spot, which lands on whatever the
   * modal was covering (a mission marker, a map node…) and "opens
   * another mission". Swallow exactly that one click, capture-phase.
   */
  function swallowNextClick() {
    const swallow = (e: Event) => {
      e.stopPropagation()
      e.preventDefault()
    }
    document.addEventListener('click', swallow, { capture: true, once: true })
    // Safety valve: if no click follows (mouse leaves, etc.), disarm.
    setTimeout(() => document.removeEventListener('click', swallow, { capture: true }), 400)
  }

  function up(event: PointerEvent) {
    const start = downAt
    downAt = null
    if (!start || event.target !== event.currentTarget) return
    if (Math.hypot(event.clientX - start.x, event.clientY - start.y) < GUTTER_TOLERANCE) {
      swallowNextClick()
      onDismiss()
    }
  }

  return { down, up, cancel }
}
